import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { ButtonLink } from '../components/ButtonLink';
import { CredentialBadges } from '../components/CredentialBadges';
import { CtaBand } from '../components/CtaBand';
import { LinkedInIcon } from '../components/LinkedInIcon';
import { contactDetails, institutions, metrics, portraitUrl, professionalTitle } from '../data/content';
import { services } from '../data/services';
import { methodology, testimonials } from '../data/experience';
const ease = [0.23, 1, 0.32, 1] as const;
const highlights = ['25+ Years of Leadership Experience', '1,150+ Careers Elevated', 'Ph.D. Level Guidance & Triple-Certified Expertise'];
export function Home() {
  const [featured, ...supporting] = testimonials;
  return <>
      {/* Hero */}
      <section className="bg-ivory" data-id="element-243" data-mp="3kxd276lgy509">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 pt-14 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:pb-24 lg:pt-20" data-id="element-244" data-mp="3owce5b5mb217">
          <motion.div className="lg:col-span-7" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-245" data-mp="30w9n136vwozp">
            <p className="flex items-center gap-3 text-sm font-medium text-steel" data-id="element-246" data-mp="2qwsiwq7a6j5u">
              <span className="h-px w-10 bg-gold" aria-hidden="true" data-id="element-247" data-mp="2mwgy9dex4iud" />
              Dr. Tom Vecchione, Ph.D.
            </p>
            <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-navy sm:text-5xl lg:text-[3.55rem]" data-id="element-248" data-mp="2i00awlfjm8ic">
              Executive Career Architecture &amp; Strategic Positioning for{' '}
              <em className="font-medium italic" data-id="element-249" data-mp="288du89kfrh87">High-Impact Leaders</em>
            </h1>
            <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-steel" data-id="element-250" data-mp="23alx914xp4x5">{professionalTitle}</p>
            <ul className="mt-6 flex flex-col gap-2 text-[15px] font-medium text-charcoal sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4" data-id="element-251" data-mp="1z6mkmtmryq92">
              {highlights.map((h, i) => <li key={h} className="flex items-center gap-4" data-id="element-252" data-mp="1kj5tfs21qzho" data-mpi={i}>
                  {i > 0 && <span className="hidden h-1 w-1 rotate-45 bg-gold sm:block" aria-hidden="true" data-id="element-253" data-mp="1ondixrgbwjgy" />}
                  {h}
                </li>)}
            </ul>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-254" data-mp="1fldysqa0oz9s">
              <ButtonLink to="/contact" data-id="element-255" data-mp="1fnqip8zc5jph">
                Schedule Executive Strategy Session
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-256" data-mp="23dbdcp4uq5el" />
              </ButtonLink>
              <ButtonLink to="/services" variant="secondary" data-id="element-257" data-mp="28ce6pcbgyn6q">
                Explore Advisory Services
              </ButtonLink>
            </div>
            <div className="mt-14 border-t border-hairline pt-6" data-id="element-258" data-mp="1yjqbgm050ra4">
              <p className="text-xs font-medium text-steel" data-id="element-259" data-mp="1yi5jn457jmfy">Former Associate VP &amp; Executive Director at</p>
              <ul className="mt-3 flex flex-wrap gap-x-8 gap-y-2 font-serif text-[17px] text-navy/80" data-id="element-260" data-mp="2r0j5e5d9rhyo">
                {institutions.map(i => <li key={i} data-id="element-261" data-mp="fk97pemcsnkh">{i}</li>)}
              </ul>
            </div>
          </motion.div>

          <motion.div className="mx-auto w-full max-w-md lg:col-span-5 lg:ml-auto lg:max-w-[440px]" initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease,
          delay: 0.08
        }} data-id="element-262" data-mp="amh5nrrzvl2k">
            <figure className="border border-hairline bg-white p-3 shadow-[0_1px_2px_rgba(11,42,85,0.05),0_20px_50px_-20px_rgba(11,42,85,0.25)]" data-id="element-263" data-mp="3ssvtcig2f9e5">
              <img src={portraitUrl} alt="Dr. Tom Vecchione, Executive Career Advising Specialist" className="aspect-square w-full object-cover object-top" data-id="element-264" data-mp="5nmkwkagvmft" />
              <figcaption className="flex items-center justify-between gap-4 px-2 pb-1 pt-4" data-id="element-265" data-mp="1h29w14yvruxu">
                <div data-id="element-266" data-mp="1c486q2av2zdx">
                  <p className="font-serif text-lg font-semibold text-navy" data-id="element-267" data-mp="1c2or8dx14sd7">Dr. Tom Vecchione</p>
                  <p className="text-sm text-steel" data-id="element-268" data-mp="27ey8bshvprwh">Woodstock, Georgia</p>
                </div>
                <a href={contactDetails.linkedin} target="_blank" rel="noreferrer" aria-label="Dr. Tom Vecchione on LinkedIn" className="flex h-10 w-10 items-center justify-center bg-[#0A66C2] text-white transition-opacity duration-150 hover:opacity-90" data-id="element-269" data-mp="2ce138k45ahqq">
                  <LinkedInIcon className="h-5 w-5" data-id="element-270" data-mp="2vbu5dk6y2nn2" />
                </a>
              </figcaption>
            </figure>
            <div className="bg-navy px-6 py-5" data-id="element-271" data-mp="3020urm9a6c6d">
              <CredentialBadges data-id="element-272" data-mp="303c564x8ei6g" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact bar */}
      <section className="bg-navy" aria-label="Proven impact" data-id="element-273" data-mp="3a0qdticwy2o7">
        <dl className="mx-auto grid max-w-7xl grid-cols-2 px-6 lg:grid-cols-4 lg:px-8" data-id="element-274" data-mp="3a2kycvk9vsrr">
          {metrics.map((m, i) => <div key={m.label} className={`py-10 pr-6 lg:py-12 ${i % 2 === 1 ? 'pl-6 border-l border-white/10' : ''} ${i > 1 ? 'border-t border-white/10 lg:border-t-0' : ''} ${i === 2 ? 'lg:border-l lg:pl-6' : ''}`} data-id="element-275" data-mp="3dzz2w9x0dcdf" data-mpi={i}>
              <dt className="sr-only" data-id="element-276" data-mp="tgebu2nkgxtk">{m.label}</dt>
              <dd data-id="element-277" data-mp="og8q133za5je">
                <span className="font-serif text-4xl text-white lg:text-[2.75rem]" data-id="element-278" data-mp="ogrvhkcfz1gj">{m.value}</span>
                {m.unit && <span className="ml-2 text-sm font-semibold text-gold" data-id="element-279" data-mp="1m25zyc4al2jq">{m.unit}</span>}
                <p className="mt-3 max-w-[15rem] text-sm leading-relaxed text-white/70" data-id="element-280" data-mp="1h4ebjs2uvyka">{m.label}</p>
              </dd>
            </div>)}
        </dl>
      </section>

      {/* Services matrix */}
      <section className="bg-white" aria-labelledby="services-heading" data-id="element-281" data-mp="17djl0bikuct1">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 lg:grid-cols-12 lg:px-8 lg:py-32" data-id="element-282" data-mp="24uictssknm84">
          <div className="lg:col-span-4" data-id="element-283" data-mp="201p834x3sdw1">
            <div className="lg:sticky lg:top-32" data-id="element-284" data-mp="200nogc20uj2p">
              <h2 id="services-heading" className="font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-285" data-mp="3ubnd8iwdysrt">
                Advisory built for the moments that define a career.
              </h2>
              <p className="mt-6 leading-relaxed text-steel" data-id="element-286" data-mp="3klkj2fdj38rw">
                Four integrated disciplines, each delivered personally and grounded in behavioral science — so your
                story is as compelling in the boardroom as it is on paper.
              </p>
              <Link to="/services" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy underline decoration-gold underline-offset-[6px] hover:decoration-2" data-id="element-287" data-mp="3flg4iuw6ko4b">
                View all advisory services
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-288" data-mp="36n46i7szio1u" />
              </Link>
            </div>
          </div>
          <div className="grid gap-x-12 sm:grid-cols-2 lg:col-span-8" data-id="element-289" data-mp="14td99hnq1i1x">
            {services.map(s => <Link key={s.id} to={`/services#${s.id}`} className="group flex flex-col border-t border-hairline py-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold" data-id="element-290" data-mp="1ex21bmdui5q0">
                <s.icon className="h-6 w-6 text-gold" strokeWidth={1.5} aria-hidden="true" data-id="element-291" data-mp="1izf88e4bw9od" />
                <h3 className="mt-6 font-serif text-xl leading-snug text-navy" data-id="element-292" data-mp="1xo2yc4venr6x">{s.title}</h3>
                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-steel" data-id="element-293" data-mp="1soyr3js1k3j8">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy" data-id="element-294" data-mp="1srchvgfoutkz">
                  Learn more
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-crisp group-hover:translate-x-1" aria-hidden="true" data-id="element-295" data-mp="22mwe63y5zua9" />
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-y border-hairline bg-ivory" aria-labelledby="method-heading" data-id="element-296" data-mp="wtjx24gchi71">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-28" data-id="element-297" data-mp="11rv3e57flaki">
          <div className="max-w-2xl" data-id="element-298" data-mp="16jmm5tkcic03">
            <h2 id="method-heading" className="font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-299" data-mp="16koagzqdguzr">
              A disciplined, three-phase methodology.
            </h2>
            <p className="mt-5 leading-relaxed text-steel" data-id="element-300" data-mp="dw8vgd78qcof">
              Every engagement follows the same rigorous framework — refined across 1,150+ executive careers.
            </p>
          </div>
          <ol className="mt-16 grid gap-12 md:grid-cols-3 md:gap-10" data-id="element-301" data-mp="mv43zvxrbyuw">
            {methodology.map(m => <li key={m.step} className="relative" data-id="element-302" data-mp="rwjqlsfl2dfe">
                <div className="flex items-center gap-4" data-id="element-303" data-mp="1ybatu2s6iacl">
                  <span className="font-serif text-4xl text-gold" data-id="element-304" data-mp="1yasomgnjglsj">{m.step}</span>
                  <span className="h-px flex-1 bg-hairline" aria-hidden="true" data-id="element-305" data-mp="238kihg7quhzd" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-navy" data-id="element-306" data-mp="2ddkgnrescphq">{m.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-steel" data-id="element-307" data-mp="28g0s9yodiwc2">{m.summary}</p>
              </li>)}
          </ol>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white" aria-labelledby="testimonials-heading" data-id="element-308" data-mp="1zngz3dqupjym">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32" data-id="element-309" data-mp="1unb5vt4xnpef">
          <h2 id="testimonials-heading" className="max-w-2xl font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-310" data-mp="1ulhy2ail2z4u">
            Trusted by leaders at the most consequential points in their careers.
          </h2>
          <div className="mt-14 grid gap-6 lg:grid-cols-12" data-id="element-311" data-mp="1gntcf0goznyq">
            <figure className="flex flex-col bg-navy p-10 text-white lg:col-span-7 lg:p-14" data-id="element-312" data-mp="1gpe8iuinneyp">
              <span className="font-serif text-6xl leading-none text-gold" aria-hidden="true" data-id="element-313" data-mp="3f2waw3a2mp1b">
                &ldquo;
              </span>
              <blockquote className="mt-4 flex-1 font-serif text-2xl leading-snug lg:text-[1.75rem]" data-id="element-314" data-mp="col60faex3e8">
                {featured.quote}
              </blockquote>
              <figcaption className="mt-10 flex flex-wrap items-end justify-between gap-4 border-t border-white/15 pt-6" data-id="element-315" data-mp="8mgjs1man91m">
                <div data-id="element-316" data-mp="mkeu8vxhulbu">
                  <p className="font-semibold" data-id="element-317" data-mp="hnxvz8wkyql1">{featured.name}</p>
                  <p className="text-sm text-white/70" data-id="element-318" data-mp="hlle0vqtog7z">{featured.title}</p>
                </div>
                <p className="text-sm font-semibold text-gold" data-id="element-319" data-mp="3q1evbcsz9vq3">{featured.outcome}</p>
              </figcaption>
            </figure>
            <div className="grid gap-6 lg:col-span-5" data-id="element-320" data-mp="3nn2dy3vyxw6">
              {supporting.map(t => <figure key={t.name} className="flex flex-col border border-hairline p-8" data-id="element-321" data-mp="3v2uh572zc69j">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-dark" data-id="element-322" data-mp="37d05utyb6pre">{t.outcome}</p>
                  <blockquote className="mt-4 flex-1 leading-relaxed text-charcoal" data-id="element-323" data-mp="32e6noo82uakw">&ldquo;{t.quote}&rdquo;</blockquote>
                  <figcaption className="mt-6" data-id="element-324" data-mp="1hvmjlmge93kd">
                    <p className="font-semibold text-navy" data-id="element-325" data-mp="1htrx43ph53e6">{t.name}</p>
                    <p className="text-sm text-steel" data-id="element-326" data-mp="1mo5to9fvhgkf">{t.title}</p>
                  </figcaption>
                </figure>)}
            </div>
          </div>
        </div>
      </section>

      <CtaBand data-id="element-327" data-mp="1cxkvgn442e21" />
    </>;
}